import cx from 'classnames';
import React from "react";
import useBuildVariantProps from '../../../modules/theme/useBuildVariantProps';
import { Icon } from '../../atoms';
import { WrapTextNodes } from '../../utils/WrapTextNodes';
import styles from './Accordion.module.scss';
import type { AccordionDetailsProps, AccordionProps, AccordionSummaryProps } from './Accordion.types';

const AccordionSummary = (props: AccordionSummaryProps) => {
  const { children, className, expandIcon } = props;

  const expandCollapseIcon = expandIcon || <Icon icon="ChevronRight" className={styles.icon} style='Outlined' />;

  const variantProps = useBuildVariantProps(
    'accordion-summary',
    {},
  );

  return (
    <summary className={cx(styles.summary, className)} {...variantProps}>
      <WrapTextNodes whiteSpaceNoWrap={true} className={styles.summaryText}>{children}</WrapTextNodes>
      <span className={styles.expandIcon}>
        {expandCollapseIcon}
      </span>
    </summary>
  );
};

const AccordionDetails = (props: AccordionDetailsProps) => {
  const variantProps = useBuildVariantProps(
    'accordion-details',
    {},
  );

  return (
    <div className={cx(styles.content, props.className)} {...variantProps}>
      <WrapTextNodes whiteSpaceNoWrap={true} className={styles.detailsText}>{props.children}</WrapTextNodes>
    </div>
  );
};

const Accordion = (props: AccordionProps) => {
  const {
    name,
    children,
    size = 'Medium',
    className,
    defaultExpanded = false,
    expanded,
    onChange,
  } = props;

  const [isOpen, setIsOpen] = React.useState(defaultExpanded);

  React.useEffect(() => {
    if (expanded !== undefined) {
      setIsOpen(expanded);
    }
  }, [expanded]);

  const handleToggle = (e: React.SyntheticEvent<HTMLDetailsElement>) => {
    const willBeOpen = (e.currentTarget as HTMLDetailsElement).open;

    if (expanded !== undefined) {
      // If expanded prop is provided, prevent default behavior
      e.preventDefault();
    } else {
      setIsOpen(willBeOpen);
    }

    onChange?.(willBeOpen);
  };

  const variantProps = useBuildVariantProps(
    'accordion',
    {
      size,
      type: isOpen ? 'Expanded' : 'Collapsed',
    },
  );

  return (
    <details
      className={cx(styles.root, className)}
      open={expanded || isOpen ? true : undefined}
      onToggle={handleToggle}
      name={name}
      {...variantProps}
    >
      <WrapTextNodes className={styles.text}>{children}</WrapTextNodes>
    </details>
  );
};

export { Accordion, AccordionDetails, AccordionSummary };

